import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useMutation } from '@/hooks/use-api';
import { removeUndefinedProperties } from '@/utils/utils';
import { requiredValidationMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import { FiPercent } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import createDealerPriceApi from '@/api/post/dealer/create-dealer-price';
import Checkbox from '../react-hook-form-inputs/checkbox/checkbox-component';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalParentRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
  padding: '24px',
};

const styledCol = {
  paddingLeft: '0px',
};

const AddPricingForm = ({ selectedRows, isDealer }) => (
  <Row style={modalParentRowStyle}>
    {selectedRows.length === 0 && (
      <Row>
        <Col xs={12}>
          <Input
            disabled={false}
            label="Product"
            placeholder="Please enter product name"
            type="text"
            name="price.name"
            registerOptions={{
              required: true,
            }}
          />
        </Col>
        <Col xs={12}>
          <Input
            disabled={false}
            label="Price"
            placeholder="Please enter price"
            type="text"
            name="price.cost_price"
            registerOptions={{
              required: true,
              valueAsNumber: true,
            }}
          />
        </Col>
        <Col xs={12}>
          <Input
            label="Part Number"
            placeholder="Please enter part number"
            type="text"
            name="price.part_number"
            registerOptions={{
              required: true,
            }}
          />
        </Col>
        {!isDealer && (
          <Col xs={12}>
            <Checkbox
              disabled={false}
              label="Temporary Part Number"
              name="price.temp_pn"
            />
          </Col>
        )}
      </Row>
    )}
    <Row style={modalRowStyle}>
      <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
        <Input
          disabled={false}
          label="End User Gross Profit - GP%"
          placeholder="%"
          type="number"
          iconRight={<FiPercent />}
          name="price.end_user_gross_profit"
          registerOptions={{
            required: requiredValidationMessage,
            valueAsNumber: true,
            max: 99.99,
            min: 0,
          }}
        />
      </Col>
    </Row>
    <Row style={modalRowStyle}>
      <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
        <Input
          disabled={false}
          label="Transfer Price Gross Profit - GP%"
          placeholder="%"
          type="number"
          iconRight={<FiPercent />}
          name="price.transfer_price_gross_profit"
          registerOptions={{
            valueAsNumber: true,
            required: requiredValidationMessage,
            max: 99.99,
            min: 0,
          }}
        />
      </Col>
    </Row>
  </Row>
);

AddPricingForm.propTypes = {
  selectedRows: PropTypes.array.isRequired,
  isDealer: PropTypes.bool,
};

AddPricingForm.defaultProps = {
  isDealer: false,
};

/**
 * @param {object} root0 - The root object containing props
 * @param {Function} root0.onClose - Callback function to close the modal
 * @param {string} root0.companyId - The ID of the supplier or Company to edit
 * @param {bool} root0.isDealer - if company is a dealer;
 * @param {Array} root0.selectedRows - The an array of ID of the prices to edit
 * @returns {JSX.Element} - The rendered JSX element
 */
const AddPricingModal = ({ isDealer, companyId, selectedRows, onClose }) => {
  const navigate = useNavigate();

  const [createPrice, { loading: createPriceLoading }] =
    useMutation(createDealerPriceApi);

  const submitForm = (values) => {
    createPrice(
      companyId,
      removeUndefinedProperties({
        end_user_gross_profit: parseFloat(values.price.end_user_gross_profit),
        transfer_price_gross_profit: parseFloat(
          values.price.transfer_price_gross_profit,
        ),
        name: values.price.name,
        cost_price: parseFloat(values.price.cost_price),
        part_number: values.price.part_number,
        temp_pn: values.price.temp_pn,
      }),
    ).then(() => {
      onClose();
      navigate(`/app/company-management/view/${companyId}/pricing`);
      Alert({
        icon: 'success',
        text: 'Price created successfully',
      });
    });
  };

  if (createPriceLoading) {
    return <Loader size={50} loading />;
  }

  return (
    <FormWrapper
      defaultValues={{
        price: {
          end_user_gross_profit: 0,
          transfer_price_gross_profit: 0,
        },
      }}
      onSubmit={submitForm}
    >
      <AddPricingForm
        selectedRows={selectedRows}
        companyId={companyId}
        isDealer={isDealer}
      />
      <SubmitCard
        padding="0"
        primaryButtonTitle="Save"
        primaryButtonType="submit"
        primaryButtonDisabled={false}
        primaryButtonWidth="112px"
        submitClick={() => {}}
        submitClickSecondary={() => {
          onClose();
        }}
        secondaryButtonType="button"
        secondaryButtonDisabled={false}
        secondaryButtonWidth="112px"
        disabled={false}
        secondaryButtonTitle="Cancel"
      />
    </FormWrapper>
  );
};

AddPricingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  companyId: PropTypes.string.isRequired,
  isDealer: PropTypes.bool.isRequired,
};

export default AddPricingModal;
